import type React from 'react'
import { createContext } from 'react'

import { type MoneyAmount } from '../types'
import { type ReportingReport } from './web_api.schemas'

/*
 ****************** Types ******************
 */

export type Direction = 'debit' | 'credit'

export type InvoiceItem = {
  id: string;
  member_id: string;
  source_type: string;
  amount: MoneyAmount;
  pretax: boolean;
  expense_type: string;
}

export type Substantiation = {
  id: string;
  review_status: string;
  is_health_insurance_premium: boolean;
  is_dental_insurance_premium: boolean;
  is_vision_insurance_premium: boolean;
  is_other_insurance_premium: boolean;
}

export type ChargeOrTxn = {
  id: string;
  member_id: string;
  amount: MoneyAmount;
  created_at: string;
  direction: Direction;
  summary: string;
  invoice_items: InvoiceItem[];
  substantiation?: Substantiation;
}

export type MemberInvoiceAmounts = {
  total: MoneyAmount;
  total_excluding_prefunding: MoneyAmount;
  employer_contributions: MoneyAmount;
  employee_deductions: MoneyAmount;
  premiums: MoneyAmount;
  prefunding: MoneyAmount;
  other: MoneyAmount;
  pretax: MoneyAmount;
  posttax: MoneyAmount;
  total_insurance_premiums: MoneyAmount;
  coverage_corrections: MoneyAmount;
}

export type MemberInvoice = {
  id: string;
  member_id: string;
  employer_invoice_id: string;
  posttax_employee_deduction: MoneyAmount;
  posttax_employee_premium_overage: MoneyAmount;
  posttax_employee_qme_overage: MoneyAmount;
  premium_prefunding: MoneyAmount;
  premium_prefunding_release: MoneyAmount;
  pretax_employee_medical_premium_overage: MoneyAmount;
  pretax_employer_premium_reimbursement: MoneyAmount;
  pretax_employer_qme_reimbursement: MoneyAmount;
  hsa_employer_contribution: MoneyAmount;
  hsa_employee_deduction: MoneyAmount;
  medical_premiums_total: MoneyAmount;
  dental_premiums_total: MoneyAmount;
  vision_premiums_total: MoneyAmount;
  amounts: MemberInvoiceAmounts;
  plan_year: number | undefined;
}

export type EmployerInvoiceAmounts = {
  premiums: MoneyAmount;
  prefunding: MoneyAmount;
  other: MoneyAmount;
  insurance_premiums_total: MoneyAmount;
  coverage_corrections: MoneyAmount;
}

export type EmployerInvoiceType = {
  id: string;
  status: string;
  period_start_date: string;
  period_end_date: string;
  total: MoneyAmount;
  member_invoices: MemberInvoice[];
  amounts: EmployerInvoiceAmounts;
  plan_years: number[];
}

export type Refund = {
  id: string;
  member_id: string;
  amount: MoneyAmount;
  created_at: string;
  state: string;
  original_charge_or_txn?: ChargeOrTxn;
  refund_transaction_id: string;
  invoice_items: InvoiceItem[];
}

export type Member = {
  id: string;
  full_name: string;
  employee_id: string;
}

export type EmployerInvoiceData = {
  employer_invoice: EmployerInvoiceType;
  members: Member[];
  charge_or_txns: ChargeOrTxn[];
  refunds: Refund[];
  premium_prefunding_invoice_items: InvoiceItem[];
  premium_prefunding_release_invoice_items: InvoiceItem[];
  latest_report?: ReportingReport | undefined;
}

export type EnrichedMemberInvoice = MemberInvoice & {
  full_name: string;
  employee_id: string;
}

/*
 ****************** Context ******************
 */

type setTargetMemberInvoiceDispatch = React.Dispatch<
  React.SetStateAction<string | null>
>

type EmployerInvoiceContextType = {
  setTargetMemberInvoice: setTargetMemberInvoiceDispatch;
}

export const EmployerInvoiceContext = createContext<EmployerInvoiceContextType>(
  {
    setTargetMemberInvoice: () => {},
  }
)

/*
 ****************** Get enriched member invoice data ****************
 */
export function enrichMemberInvoices (
  data: EmployerInvoiceData
): EnrichedMemberInvoice[] {
  const MemberById = data.members.reduce<{ [id: string]: Member }>((acc, member) => {
    acc[member.id] = member
    return acc
  }, {})
  return data.employer_invoice.member_invoices.map((memberInvoice) => {
    return {
      ...memberInvoice,
      // Default to empty string for the following columns to ensure they aren't skipped by global search
      // (which skips columns where the first row value is null even if global filtering is enabled).
      full_name: MemberById[memberInvoice.member_id].full_name || '',
      employee_id: MemberById[memberInvoice.member_id].employee_id || '',
    }
  })
}

const ICHRA_EXPENSE_TYPES = [
  'premium_reimbursement',
  'premium_overage',
  'premium_overage_recoupment',
]
const QME_EXPENSE_TYPES = ['qme_reimbursement', 'qme_overage']
const CARD_BASED_TRANSACTION_SUBTYPES = [
  'PurchaseTransaction',
  'CardTransaction',
]

export function employerInvoiceRefundLineItemDescription (refund: Refund) {
  if (refund.original_charge_or_txn) {
    return `Refund for ${employerInvoiceTransactionLineItemDescription(
      refund.original_charge_or_txn
    )}`
  } else {
    return 'Refund'
  }
}

/*
  HasTransactionsMetadata allows us to use this method on any Transaction type that has invoice items and substantiations,
  regardless of the Transaction fields available (since these vary per context).
*/

interface HasTransactionMetadata {
  invoice_items: InvoiceItem[];
  substantiation?: Substantiation;
}

export function employerInvoiceTransactionLineItemDescription<
  T extends HasTransactionMetadata
> (transaction: T) {
  if (transaction.substantiation) {
    if (transaction.substantiation.is_health_insurance_premium) {
      return 'Medical Premium'
    } else if (transaction.substantiation.is_dental_insurance_premium) {
      return 'Dental Premium'
    } else if (transaction.substantiation.is_vision_insurance_premium) {
      return 'Vision Premium'
    }
  }

  if (
    transaction.invoice_items.find((item) =>
      ICHRA_EXPENSE_TYPES.includes(item.expense_type)
    )
  ) {
    return 'Insurance Premium'
  } else if (
    transaction.invoice_items.find((item) =>
      QME_EXPENSE_TYPES.includes(item.expense_type)
    )
  ) {
    return 'QME'
  } else if (
    transaction.invoice_items.length > 0 &&
    transaction.invoice_items.every((item) =>
      CARD_BASED_TRANSACTION_SUBTYPES.includes(item.source_type)
    )
  ) {
    return 'Post-tax spending'
  } else {
    return 'Other spending'
  }
}
